import workflow from '@banking/ui/lib/workflow';
import { recovery } from '@banking/ui/lib/workflow/experiments';
import { ListDDSEligibleUnitsRequest, OnboardToInstantAccessRequest } from '@square/dashboard-protos/models/squareup/inflows/service/external_instant_access_service';
import { GetCheckingDirectDepositAccountRequest } from '@square/dashboard-protos/models/squareup/bbfrontend/common/checking_direct_deposit_account/checking_direct_deposit_account';
import { InstantAccessProgram } from '@square/dashboard-protos/models/squareup/inflows/instant_access_program_model';
import assert, { assertNonNullable } from '@banking/ui/utils/assert';

/**
 * Error handling in this workflow follows these patterns:
 *
 * assert: Used for programming bugs that should never happen in correct code.
 * - Crashes are intended to force fixes and prevent corrupted state
 * - No recovery attempted since program state may be invalid
 *
 * setErrorAction: Used for expected errors during normal operation.
 * - Handles cases like API failures or business logic conditions
 * - Shows a generic error screen that allows closing/restarting the flow
 * - Detailed error messages can be added in the future if designs change
 */

// Enums
export let Screen = /*#__PURE__*/function (Screen) {
  Screen["Loading"] = "SCREEN_LOADING";
  Screen["SetupPage"] = "SCREEN_SETUP_PAGE";
  Screen["AccountRoutingModal"] = "SCREEN_ACCOUNT_ROUTING_MODAL";
  Screen["Error"] = "SCREEN_ERROR";
  return Screen;
}({});
export let NavigationIcon = /*#__PURE__*/function (NavigationIcon) {
  NavigationIcon["Close"] = "Close";
  NavigationIcon["GoBack"] = "GoBack";
  return NavigationIcon;
}({});
export let ActionType = /*#__PURE__*/function (ActionType) {
  ActionType["Initialize"] = "DDS_MODAL/INITIALIZE";
  ActionType["NextScreen"] = "DDS_MODAL/NEXT_SCREEN";
  ActionType["PreviousScreen"] = "DDS_MODAL/PREVIOUS_SCREEN";
  ActionType["CloseModal"] = "DDS_MODAL/CLOSE_MODAL";
  ActionType["SetError"] = "DDS_MODAL/SET_ERROR";
  ActionType["ListDDSEligibleUnitsReceived"] = "DDS_MODAL/LIST_DDS_ELIGIBLE_UNITS_RECEIVED";
  ActionType["ListDDSEligibleUnitsRejected"] = "DDS_MODAL/LIST_DDS_ELIGIBLE_UNITS_REJECTED";
  ActionType["OnLocationChange"] = "DDS_MODAL/ON_LOCATION_CHANGE";
  ActionType["OnSwitchPayouts"] = "DDS_MODAL/ON_SWITCH_PAYOUTS";
  ActionType["OnboardToInstantAccessReceived"] = "DDS_MODAL/ONBOARD_TO_INSTANT_ACCESS_RECEIVED";
  ActionType["OnboardToInstantAccessRejected"] = "DDS_MODAL/ONBOARD_TO_INSTANT_ACCESS_REJECTED";
  ActionType["GetCheckingDirectDepositAccount"] = "DDS_MODAL/GET_CHECKING_DIRECT_DEPOSIT_ACCOUNT";
  ActionType["GetCheckingDirectDepositAccountReceived"] = "DDS_MODAL/GET_CHECKING_DIRECT_DEPOSIT_ACCOUNT_RECEIVED";
  ActionType["GetCheckingDirectDepositAccountRejected"] = "DDS_MODAL/GET_CHECKING_DIRECT_DEPOSIT_ACCOUNT_REJECTED";
  return ActionType;
}({});

// Types

export const ACCOUNT_ROUTING_FLOW = [Screen.SetupPage, Screen.AccountRoutingModal];

// Interfaces

// Action interfaces

// Action type union

// Helper functions
export const hasMultipleLocations = state => state.eligibleUnitInfos.length > 1;
export const getUnitToken = state => state.selectedUnit?.token ?? '';
const findEligibleUnitWithIntegrations = units => units.find(unit => unit.integrationToOptInStatus.length > 0);
const configureLocationSelector = (context, eligibleUnitInfos, selectedUnitInfo) => {
  context.locationSelector.configure({
    shownUnits: eligibleUnitInfos.map(unitInfo => unitInfo.unitToken),
    tokens: [selectedUnitInfo.unitToken],
    hideInactiveUnits: true,
    single: true
  });
};
const findSelectedUnit = (locationSelector, unitToken) => {
  const unit = locationSelector.units.find(unit => unit.token === unitToken);
  assert(unit, `Unit with token ${unitToken} not found in LocationSelectorService`);
  return unit;
};
const findSelectedUnitInfo = (unitToken, eligibleUnitInfos) => eligibleUnitInfos.find(unitInfo => unitInfo.unitToken === unitToken);

// Action creators
export const initializeAction = () => ({
  type: ActionType.Initialize
});
export const nextScreenAction = () => ({
  type: ActionType.NextScreen
});
export const previousScreenAction = () => ({
  type: ActionType.PreviousScreen
});
export const closeModalAction = () => ({
  type: ActionType.CloseModal
});
export const setErrorAction = error => ({
  type: ActionType.SetError,
  error
});
export const listDDSEligibleUnitsReceivedAction = response => ({
  type: ActionType.ListDDSEligibleUnitsReceived,
  response
});
export const listDDSEligibleUnitsRejectedAction = error => ({
  type: ActionType.ListDDSEligibleUnitsRejected,
  error
});
export const onLocationChangeAction = units => ({
  type: ActionType.OnLocationChange,
  units
});
export const onSwitchPayoutsAction = integration => ({
  type: ActionType.OnSwitchPayouts,
  integration
});
export const onboardToInstantAccessReceivedAction = response => ({
  type: ActionType.OnboardToInstantAccessReceived,
  response
});
export const onboardToInstantAccessRejectedAction = error => ({
  type: ActionType.OnboardToInstantAccessRejected,
  error
});
export const getCheckingDirectDepositAccountAction = unitToken => ({
  type: ActionType.GetCheckingDirectDepositAccount,
  unitToken
});
export const getCheckingDirectDepositAccountReceivedAction = response => ({
  type: ActionType.GetCheckingDirectDepositAccountReceived,
  response
});
export const getCheckingDirectDepositAccountRejectedAction = error => ({
  type: ActionType.GetCheckingDirectDepositAccountRejected,
  error
});

// State initialization
export const INITIAL_STATE = {
  navigation: {
    currentScreen: Screen.Loading,
    flow: []
  },
  loading: false,
  modalOpen: false,
  availableIntegrations: [],
  selectedIntegration: null,
  eligibleUnitInfos: [],
  selectedUnit: null,
  isOnboardingInstantAccess: false,
  directDepositAccountInfo: null
};
export const createState = (overrides = {}) => ({
  ...INITIAL_STATE,
  ...overrides
});

// Action handlers
function handleListDDSEligibleUnitsReceived(state, action, context) {
  const {
    response
  } = action;
  if (!response.success) {
    return [state, () => setErrorAction(new Error('Failed to list eligible units'))];
  }
  const {
    units: eligibleUnitInfos
  } = response.success;
  if (eligibleUnitInfos.length === 0) {
    return [state, () => setErrorAction(new Error('No eligible units found'))];
  }
  const selectedUnitInfo = findEligibleUnitWithIntegrations(eligibleUnitInfos);
  if (!selectedUnitInfo) {
    return [state, () => setErrorAction(new Error('No eligible integrations found'))];
  }
  const availableIntegrations = [...selectedUnitInfo.integrationToOptInStatus];
  configureLocationSelector(context, eligibleUnitInfos, selectedUnitInfo);

  // Find the selected Unit from the locationSelector service
  const selectedUnit = findSelectedUnit(context.locationSelector, selectedUnitInfo.unitToken);
  return [{
    ...state,
    availableIntegrations,
    selectedUnit,
    navigation: {
      ...state.navigation,
      currentScreen: Screen.SetupPage
    },
    loading: false,
    eligibleUnitInfos
  }, null];
}
function handleListDDSEligibleUnitsRejected(state, action) {
  return [state, () => setErrorAction(action.error)];
}
function handleInitialize(state, context) {
  const work = () => context.externalInstantAccessApi.client.listDDSEligibleUnits(ListDDSEligibleUnitsRequest.create({
    merchantToken: context.merchantToken
  })).then(listDDSEligibleUnitsReceivedAction, listDDSEligibleUnitsRejectedAction);
  return [{
    ...state,
    modalOpen: true,
    loading: true
  }, work];
}
function handleNextScreen(state) {
  const currentIndex = state.navigation.flow.indexOf(state.navigation.currentScreen);

  // If we're at the end of any flow, close the modal
  if (currentIndex === state.navigation.flow.length - 1) {
    return [state, () => closeModalAction()];
  }
  const nextScreen = state.navigation.flow[currentIndex + 1];
  return [{
    ...state,
    navigation: {
      ...state.navigation,
      currentScreen: nextScreen
    }
  }, nextScreen === Screen.AccountRoutingModal && getUnitToken(state) ? () => getCheckingDirectDepositAccountAction(getUnitToken(state)) : null];
}
function handlePreviousScreen(state) {
  const currentIndex = state.navigation.flow.indexOf(state.navigation.currentScreen);
  if (currentIndex === 0) {
    return [state, null];
  }
  return [{
    ...state,
    navigation: {
      ...state.navigation,
      currentScreen: state.navigation.flow[currentIndex - 1]
    }
  }, null];
}
function handleCloseModal(state) {
  return [{
    ...state,
    modalOpen: false,
    navigation: {
      currentScreen: Screen.Loading,
      flow: []
    },
    selectedIntegration: null,
    selectedUnit: null,
    directDepositAccountInfo: null
  }, null];
}
function handleSetError(state, action, context) {
  context.sentry.captureException(action.error);
  return [{
    ...state,
    loading: false,
    navigation: {
      ...state.navigation,
      currentScreen: Screen.Error
    }
  }, null];
}
function handleOnLocationChange(state, action) {
  if (action.units.length === 0) {
    return [state, null];
  }
  const selectedUnit = action.units[0];
  const eligibleUnitInfo = findSelectedUnitInfo(selectedUnit.token, state.eligibleUnitInfos);
  assert(eligibleUnitInfo, 'Selected unit not found in eligible units');
  const availableIntegrations = [...eligibleUnitInfo.integrationToOptInStatus];
  return [{
    ...state,
    selectedUnit,
    availableIntegrations
  }, null];
}
function handleOnSwitchPayouts(state, context) {
  assert(state.selectedUnit, 'Selected unit is null but eligible unit info exists');
  const selectedUnit = assertNonNullable(state.selectedUnit);
  const nextState = {
    ...state,
    navigation: {
      ...state.navigation,
      flow: ACCOUNT_ROUTING_FLOW
    }
  };

  // If all integrations are already opted in, move to next screen
  const allOptedIn = state.availableIntegrations.every(i => i.isOptedIn);
  if (allOptedIn) {
    return [nextState, () => nextScreenAction()];
  }

  // If not all opted in, onboard to instant access
  return [{
    ...nextState,
    isOnboardingInstantAccess: true
  }, () => context.externalInstantAccessApi.client.onboardToInstantAccess(OnboardToInstantAccessRequest.create({
    unitTokens: [selectedUnit.token],
    optInMechanism: InstantAccessProgram.OptInMechanism.PAID_PLAN_WAIVER
  })).then(onboardToInstantAccessReceivedAction, onboardToInstantAccessRejectedAction)];
}
function handleOnboardToInstantAccessReceived(state, action) {
  const {
    response
  } = action;
  if (!response.success) {
    return [state, () => setErrorAction(new Error('Failed to onboard to Instant Access'))];
  }
  return [{
    ...state,
    isOnboardingInstantAccess: false
  }, () => nextScreenAction()];
}
function handleOnboardToInstantAccessRejected(state, action) {
  return [state, () => setErrorAction(action.error)];
}
function handleGetCheckingDirectDepositAccount(state, action, context) {
  const {
    unitToken
  } = action;
  const work = () => context.getCheckingDirectDepositAccount(GetCheckingDirectDepositAccountRequest.create({
    unitToken,
    fieldMask: {
      paths: ['data.account_number_full', 'data.routing_number']
    }
  })).then(getCheckingDirectDepositAccountReceivedAction, getCheckingDirectDepositAccountRejectedAction);
  return [{
    ...state,
    loading: true
  }, work];
}
function handleGetCheckingDirectDepositAccountReceived(state, action) {
  const {
    response
  } = action;
  assertNonNullable(response);
  if (response.error) {
    return [state, () => setErrorAction(new Error(`Failed to get checking direct deposit account: ${response.error}`))];
  }
  const directDepositAccountInfo = response.data || null;
  return [{
    ...state,
    directDepositAccountInfo,
    loading: false
  }, null];
}
function handleGetCheckingDirectDepositAccountRejected(state, action) {
  return [state, () => setErrorAction(action.error)];
}

// Workflow
export const update = context => (state, action) => {
  switch (action.type) {
    case ActionType.Initialize:
      return handleInitialize(state, context);
    case ActionType.NextScreen:
      return handleNextScreen(state);
    case ActionType.PreviousScreen:
      return handlePreviousScreen(state);
    case ActionType.CloseModal:
      return handleCloseModal(state);
    case ActionType.SetError:
      return handleSetError(state, action, context);
    case ActionType.ListDDSEligibleUnitsReceived:
      return handleListDDSEligibleUnitsReceived(state, action, context);
    case ActionType.ListDDSEligibleUnitsRejected:
      return handleListDDSEligibleUnitsRejected(state, action);
    case ActionType.OnLocationChange:
      return handleOnLocationChange(state, action);
    case ActionType.OnSwitchPayouts:
      return handleOnSwitchPayouts(state, context);
    case ActionType.OnboardToInstantAccessReceived:
      return handleOnboardToInstantAccessReceived(state, action);
    case ActionType.OnboardToInstantAccessRejected:
      return handleOnboardToInstantAccessRejected(state, action);
    case ActionType.GetCheckingDirectDepositAccount:
      return handleGetCheckingDirectDepositAccount(state, action, context);
    case ActionType.GetCheckingDirectDepositAccountReceived:
      return handleGetCheckingDirectDepositAccountReceived(state, action);
    case ActionType.GetCheckingDirectDepositAccountRejected:
      return handleGetCheckingDirectDepositAccountRejected(state, action);
    default:
      return [state, null];
  }
};
function recover(state, error) {
  // If this is an error, show the error screen
  return [{
    ...state,
    loading: false,
    navigation: {
      ...state.navigation,
      currentScreen: Screen.Error
    }
  }, error];
}
const directDepositSwitchModal = (seed, context) => {
  const w = workflow(update(context), seed);
  return {
    states: w.states.pipe(recovery(recover)),
    updates: w.updates,
    emit: w.emit
  };
};
export const getNavigationIcon = screen => {
  switch (screen) {
    case Screen.SetupPage:
      return NavigationIcon.Close;
    case Screen.AccountRoutingModal:
      return NavigationIcon.GoBack;
    case Screen.Loading:
    case Screen.Error:
    default:
      return NavigationIcon.Close;
  }
};
export default directDepositSwitchModal;